'use client';

import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text } from '../../atoms/text/text';
import { cn } from '../../lib/cn';
import { getInitials } from '../../lib/get-initials';
import { type LinkLike, NativeLink } from '../../lib/link';

/**
 * Header globale di SLIMS CER: logo, slot contestuali, indicatore live, menu
 * profilo.
 *
 * Geometria presa dal mock `SLIMS-CER_v11.html`: altezza 73px, padding `10px 20px`,
 * controlli alti 38px, icone 15px. Il gap fra i controlli di destra sta a 10px invece dei 6px
 * del mock: i divisori che lì scandivano i gruppi qui non ci sono più.
 *
 * 73px e non i 58px di `--hdr-h`: quella variabile nel mock è un residuo che nessuna
 * regola di altezza legge davvero — l'header lì non ha `height`, lo alzano i 10px di
 * padding sopra la chip utente (53px), e il conto torna a 73. Che `--hdr-h` sia
 * rimasta indietro si vede nel mock stesso, dove la sidebar `sticky top:var(--hdr-h)`
 * scivola di 15px sotto l'header appena si scrolla.
 */

/** Tinta hover/attiva di un bottone dell'header. */
export type AppHeaderButtonTone = 'accent' | 'warn' | 'green' | 'purple';

const BUTTON_TONE: Record<AppHeaderButtonTone, { hover: string; active: string }> = {
	accent: {
		hover: 'hover:border-accent hover:text-accent',
		active: 'border-accent/50 bg-accent/[.08] text-accent',
	},
	warn: {
		hover: 'hover:border-warn hover:text-warn',
		active: 'border-warn/50 bg-warn/[.08] text-warn',
	},
	green: {
		hover: 'hover:border-accent-green hover:text-accent-green',
		active: 'border-accent-green/50 bg-accent-green/[.08] text-accent-green',
	},
	purple: {
		hover: 'hover:border-accent-purple hover:text-accent-purple',
		active: 'border-accent-purple/50 bg-accent-purple/[.08] text-accent-purple',
	},
};

export interface AppHeaderButtonProps {
	/** Icona a sinistra dell'etichetta; da sola rende un bottone quadrato. */
	icon?: ReactNode;
	/** Etichetta testuale; assente → bottone icona (38×38). */
	label?: string;
	/** Stato evidenziato (es. vista Admin attiva). */
	active?: boolean;
	tone?: AppHeaderButtonTone;
	title?: string;
	'aria-label'?: string;
	/** Contatore mostrato come pallino in alto a destra (0 → nascosto). */
	badge?: number;
	onClick: () => void;
}

/**
 * Bottone azione dell'header, condiviso da entrambe le interfacce.
 *
 * L'etichetta è a 600 come la `.h-tab` del mock v11 (13px/600 in Inter): 13px e Inter
 * li dava già `Text variant="body"`, il peso era l'unico scostamento.
 */
export function AppHeaderButton({
	icon,
	label,
	active = false,
	tone = 'accent',
	title,
	'aria-label': ariaLabel,
	badge = 0,
	onClick,
}: AppHeaderButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			aria-label={ariaLabel}
			aria-pressed={active || undefined}
			className={cn(
				'relative inline-flex h-[38px] flex-none items-center justify-center gap-[7px] whitespace-nowrap rounded-btn border border-border bg-card transition-colors',
				'[&_svg]:size-[15px] [&_svg]:shrink-0',
				label ? 'px-[15px]' : 'w-[38px]',
				active ? BUTTON_TONE[tone].active : cn('text-text-muted', BUTTON_TONE[tone].hover),
			)}
		>
			{icon}
			{label ? (
				<Text as="span" variant="body" weight="semibold" tone="inherit">
					{label}
				</Text>
			) : null}
			{badge > 0 && (
				<Text
					as="span"
					variant="micro"
					weight="bold"
					tone="inherit"
					className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 font-mono text-white"
				>
					{badge > 9 ? '9+' : badge}
				</Text>
			)}
		</button>
	);
}

/** Separatore verticale fra i gruppi di controlli dell'header. */
export function AppHeaderDivider() {
	return <div className="mx-1.5 h-[26px] w-px flex-none bg-border" />;
}

export interface AppHeaderMenuItem {
	id: string;
	label: string;
	/** Icona a sinistra dell'etichetta. */
	icon?: ReactNode;
	/** Se presente la voce è un link (via `linkComponent`), altrimenti un bottone. */
	href?: string;
	onClick?: () => void;
	/** Contatore a destra dell'etichetta (0 → nascosto). */
	badge?: number;
}

export interface AppHeaderProps {
	/** Wordmark del prodotto: `CerLogo`, che gestisce da sé le varianti di tema. */
	logoSlot: ReactNode;
	/** Slot subito dopo il logo: controlli segmentati, selettori di ambito. */
	leadingSlot?: ReactNode;
	/** Bottoni azione a destra, prima del divisore (tipicamente {@link AppHeaderButton}). */
	actionsSlot?: ReactNode;
	/** Slot campanella/pannello notifiche. */
	notificationsSlot?: ReactNode;
	/** Slot selettore di configurazione, dopo le notifiche. */
	scopeSelectorSlot?: ReactNode;
	/**
	 * Etichetta dell'indicatore live: `undefined` → orologio interno "LIVE · hh:mm",
	 * una stringa → etichetta fissa, `null` → indicatore nascosto.
	 */
	liveLabel?: string | null;
	/** Nome completo dell'utente loggato. */
	fullName: string | null;
	/** Email dell'utente loggato, usata come ripiego per nome e iniziali. */
	email?: string | null;
	/** URL dell'avatar; se assente mostra le iniziali. */
	avatarUrl?: string | null;
	/** Etichetta del ruolo, mostrata sotto il nome. */
	roleLabel: string;
	/** Voci del menu profilo, in ordine. */
	menuItems: AppHeaderMenuItem[];
	/** Componente link da usare (default: link nativo `<a>`); passare il `Link` del router quando serve navigazione client-side. */
	linkComponent?: LinkLike;
}

/** Scroll verticale oltre il quale il menu dell'account si chiude. */
const MENU_SCROLL_CLOSE_PX = 40;
export function AppHeader({
	logoSlot,
	leadingSlot,
	actionsSlot,
	notificationsSlot,
	scopeSelectorSlot,
	liveLabel,
	fullName,
	email,
	avatarUrl,
	roleLabel,
	menuItems,
	linkComponent: Link = NativeLink,
}: AppHeaderProps) {
	const [menuOpen, setMenuOpen] = useState(false);
	const [time, setTime] = useState('');
	const menuRef = useRef<HTMLDivElement>(null);
	const showClock = liveLabel === undefined;

	useEffect(() => {
		if (!showClock) return;
		const update = () =>
			setTime(new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }));
		update();
		const t = setInterval(update, 1000);
		return () => clearInterval(t);
	}, [showClock]);

	useEffect(() => {
		if (!menuOpen) return;
		const close = () => setMenuOpen(false);
		const handleMouseDown = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) close();
		};
		// Si chiude quando l'utente scorre davvero la pagina, non a ogni evento di
		// scroll: su schermo stretto aprire il menu fa scorrere la pagina per
		// mostrarne le voci, e quello scroll lo richiudeva prima del tocco (INB-346).
		const startY = window.scrollY;
		const handleScroll = () => {
			if (Math.abs(window.scrollY - startY) > MENU_SCROLL_CLOSE_PX) close();
		};
		document.addEventListener('mousedown', handleMouseDown);
		window.addEventListener('scroll', handleScroll, true);
		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
			window.removeEventListener('scroll', handleScroll, true);
		};
	}, [menuOpen]);

	const liveText = showClock ? `LIVE${time ? ` · ${time}` : ''}` : liveLabel;

	return (
		<header className="sticky top-0 z-50 flex h-[73px] flex-none items-center gap-2.5 border-b border-border bg-panel/97 px-5 backdrop-blur-md">
			{logoSlot}
			{leadingSlot}

			<div className="flex-1" />

			{/* 10px e non i 6px del mock: lì i gruppi di controlli erano staccati dai
			    divisori, tolti quelli il gap è l'unica cosa che li separa. Stesso valore
			    del gap esterno dell'header, così la fascia ha una spaziatura sola. */}
			<div className="flex items-center gap-2.5">
				{actionsSlot}

				{notificationsSlot}
				{notificationsSlot && <AppHeaderDivider />}

				{scopeSelectorSlot}

				{liveText != null && (
					<div className="flex flex-none items-center gap-1.5">
						<span className="h-2 w-2 animate-pulse-dot rounded-full bg-accent-green" />
						<Text as="span" variant="caption" tone="success" className="font-mono">
							{liveText}
						</Text>
					</div>
				)}

				<div ref={menuRef} className="relative">
					<button
						type="button"
						onClick={() => setMenuOpen((o) => !o)}
						aria-expanded={menuOpen}
						className="flex items-center gap-[9px] whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1.5"
					>
						<span
							className={cn(
								'flex h-[30px] w-[30px] flex-none items-center justify-center overflow-hidden rounded-full',
								avatarUrl ? 'bg-card' : 'bg-gradient-to-br from-accent to-accent-purple text-black',
							)}
						>
							{avatarUrl ? (
								<img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
							) : (
								<Text as="span" variant="caption" weight="bold" tone="inherit">
									{getInitials(fullName, email ?? null)}
								</Text>
							)}
						</span>
						<span className="text-left">
							<Text as="span" variant="body" weight="semibold" className="block">
								{fullName ?? email ?? '-'}
							</Text>
							{roleLabel && (
								<Text as="span" variant="micro" tone="success" className="block font-mono">
									{roleLabel}
								</Text>
							)}
						</span>
						<ChevronDown className="h-3 w-3 opacity-60" />
					</button>

					{menuOpen && (
						<div className="absolute right-0 top-full z-50 mt-1 min-w-[240px] rounded-lg border border-border bg-card p-1 shadow-xl">
							{menuItems.map((item) => {
								const className = cn(
									'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors',
									'[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-text-muted',
									'hover:bg-accent/10 hover:text-accent',
								);
								const content = (
									<>
										{item.icon}
										<Text as="span" variant="body" tone="inherit">
											{item.label}
										</Text>
										{item.badge ? (
											<Text
												as="span"
												variant="micro"
												weight="bold"
												tone="inherit"
												className="ml-auto rounded-full bg-warn px-1.5 py-0.5 font-mono leading-none text-white"
											>
												{item.badge > 9 ? '9+' : item.badge}
											</Text>
										) : null}
									</>
								);
								const close = () => setMenuOpen(false);
								return item.href ? (
									<Link
										key={item.id}
										href={item.href}
										className={className}
										onClick={() => {
											close();
											item.onClick?.();
										}}
									>
										{content}
									</Link>
								) : (
									<button
										key={item.id}
										type="button"
										className={className}
										onClick={() => {
											close();
											item.onClick?.();
										}}
									>
										{content}
									</button>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</header>
	);
}
